import { useEffect, useState } from "react";
import { NavLink, Outlet } from "react-router-dom";
import {
  BarChart3, Boxes, ChevronRight, ClipboardList, LayoutDashboard,
  Lightbulb, LogOut, Menu, Moon, Settings, ShieldCheck,
  ShoppingBasket, Sun, Trophy, Users, Wrench, X,
} from "lucide-react";
import type { SessionUser } from "../lib/api";
import { api } from "../lib/api";

const primaryNav = [
  { to: "/", label: "Overview", icon: LayoutDashboard },
  { to: "/sales", label: "Sales", icon: BarChart3 },
  { to: "/products", label: "Products", icon: Boxes },
  { to: "/transactions", label: "Transactions", icon: ClipboardList },
];

const phaseTwoNav = [
  { to: "/purchases", label: "Purchases", icon: ShoppingBasket },
  { to: "/operations", label: "Operations", icon: Wrench },
  { to: "/leaderboards", label: "Leaderboards", icon: Trophy },
  { to: "/staff", label: "Staff", icon: Users },
];

export function Layout({ user, onLogout }: { user: SessionUser; onLogout: () => void }) {
  const [open, setOpen] = useState(false);
  const [theme, setTheme] = useState<"dark" | "light">(() => (localStorage.getItem("theme") === "light" ? "light" : "dark"));
  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    localStorage.setItem("theme", theme);
  }, [theme]);

  const logout = async () => {
    await api("/api/auth/logout", { method: "POST" }).catch(() => undefined);
    onLogout();
  };

  return <div className="app-shell">
    <button className="mobile-menu" aria-label="Open navigation" onClick={() => setOpen(true)}><Menu /></button>
    {open && <button className="nav-backdrop" aria-label="Close navigation" onClick={() => setOpen(false)} />}
    <aside className={`sidebar ${open ? "open" : ""}`}>
      <div className="brand-block">
        <img src="https://i.gyazo.com/29aa968686bc11de8a768193c5974484.png" alt="24/7 Chumash North logo" />
        <div><strong>24/7</strong><span>Chumash North</span></div>
        <button className="close-nav" aria-label="Close navigation" onClick={() => setOpen(false)}><X size={19} /></button>
      </div>
      <nav aria-label="Main navigation">
        <span className="nav-label">Workspace</span>
        {primaryNav.map(({ to, label, icon: Icon }) => <NavLink key={to} to={to} end={to === "/"} onClick={() => setOpen(false)} className={({ isActive }) => isActive ? "active" : ""}><Icon size={18} /><span>{label}</span><ChevronRight className="nav-arrow" size={15} /></NavLink>)}
        {user.role === "owner" && <NavLink to="/admin" onClick={() => setOpen(false)} className={({ isActive }) => isActive ? "active" : ""}><Settings size={18} /><span>Admin</span><ChevronRight className="nav-arrow" size={15} /></NavLink>}
        <span className="nav-label phase-label">Deep analytics</span>
        {phaseTwoNav.map(({ to, label, icon: Icon }) => <NavLink key={to} to={to} onClick={() => setOpen(false)} className={({ isActive }) => isActive ? "active" : ""}><Icon size={18}/><span>{label}</span><ChevronRight className="nav-arrow" size={15}/></NavLink>)}
        <span className="nav-label phase-label">Phase 3</span>
        <button className="nav-disabled" disabled title="Planned for Phase 3"><Lightbulb size={18}/><span>Insights</span><em>Soon</em></button>
      </nav>
      <div className="sidebar-footer">
        <div className="private-chip"><ShieldCheck size={14} />Private dashboard</div>
        <div className="user-row">
          <div className="avatar">{user.displayName.slice(0, 1).toUpperCase()}</div>
          <div><strong>{user.displayName}</strong><span>{user.role}</span></div>
        </div>
        <div className="footer-actions">
          <button aria-label={`Switch to ${theme === "dark" ? "light" : "dark"} mode`} onClick={() => setTheme(theme === "dark" ? "light" : "dark")}>{theme === "dark" ? <Sun size={17} /> : <Moon size={17} />}<span>{theme === "dark" ? "Light" : "Dark"}</span></button>
          <button onClick={logout}><LogOut size={17} /><span>Logout</span></button>
        </div>
      </div>
    </aside>
    <main className="main-content"><Outlet /></main>
  </div>;
}
